import type { OwnedMonsterView } from '../../shared/protocol.ts';
import { MAX_MOVES, type Move } from '../../shared/moves.ts';
import { BONUS_MAX, isPerfectBonus, STAT_KEYS, STAT_LABELS } from '../../shared/stats.ts';
import type { IconName } from '../../shared/icons.ts';
import { $, el } from '../common/dom.ts';
import { icon, withIcon } from '../common/icons.ts';
import { typeBadges } from '../common/element-badge.ts';
import { Modal } from '../common/modal.ts';
import { rarityBadge } from '../common/rarity-badge.ts';
import { actionBar, hpBar, monsterName, type CardAction, type MonsterBrowse } from './monster-card.ts';
import { moveCard } from './move-card.ts';
import { creatureText } from '../../shared/game-config.ts';

export interface MonsterSheetHandlers {
  /** « Donner un objet » : ouvre le sac pour choisir ce que l'on donne au monstre. */
  onGiveItem(monster: OwnedMonsterView): void;
  onRename(monsterId: number, nickname: string): void;
  /** Ce monstre suit le joueur (null : plus aucun). */
  onFollow(monsterId: number | null): void;
  /** Nouvelles attaques du monstre (1 à 4). */
  onSetMoves(monsterId: number, moveIds: number[]): void;
}

const STAT_OF = { hp: 'maxHp', atk: 'atk', def: 'def', spd: 'spd' } as const;

type SheetTab = 'summary' | 'moves' | 'stats';
const SHEET_TABS: { id: SheetTab; label: string; icon: IconName }[] = [
  { id: 'summary', label: 'Résumé', icon: 'summary' }, { id: 'moves', label: 'Attaques', icon: 'moves' }, { id: 'stats', label: 'Stats', icon: 'stats' },
];

/**
 * Fiche d'un monstre possédé : statistiques détaillées (base de l'espèce, bonus propres au monstre,
 * total à son niveau), potentiel, expérience ; « Donner un objet » ouvre le sac.
 */
export class MonsterSheet extends Modal {
  private readonly body = $('.sheet-body', this.root);
  private readonly handlers: MonsterSheetHandlers;
  private collection: OwnedMonsterView[] = [];
  private monsterId: number | null = null;
  private catalog: ReadonlyMap<number, Move> = new Map();
  /** Attaques choisies en cours d'édition (null : pas d'édition en cours). */
  private editingMoves: number[] | null = null;
  private tab: SheetTab = 'summary';
  /** Actions de la carte d'où la fiche a été ouverte (équipe, boîte, soin…). */
  private actions: CardAction[] = [];
  /** Flèches vers les monstres voisins de la liste d'où la fiche a été ouverte. */
  private browse: MonsterBrowse | null = null;

  constructor(handlers: MonsterSheetHandlers) {
    super('#monster-sheet');
    this.handlers = handlers;
  }

  setMoveCatalog(catalog: ReadonlyMap<number, Move>): void {
    this.catalog = catalog;
    if (this.isOpen) this.render();
  }

  show(monster: OwnedMonsterView, actions: CardAction[] = [], browse?: MonsterBrowse): void {
    // En passant d'un monstre à son voisin, on reste sur le même onglet.
    const keepTab = this.isOpen && this.browse !== null && browse !== undefined;
    this.browse = browse ?? null;
    this.monsterId = monster.id;
    // Une action change la collection (ordre, boîte…) : la fiche se ferme pour ne pas garder des actions périmées.
    // Monter / Descendre et Soigner restent sur la carte : ici, les flèches passent au monstre voisin, et on donne un
    // objet depuis le sac.
    this.actions = actions.filter((action) => !action.cardOnly).map((action) => ({ ...action, onClick: () => { this.close(); action.onClick(); } }));
    this.editingMoves = null;
    if (!keepTab) this.tab = 'summary';
    this.open();
    this.render();
  }

  /** Flèches gauche / haut : monstre précédent ; droite / bas : suivant. Renvoie vrai si la touche a servi. */
  handleKey(event: KeyboardEvent): boolean {
    const step = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 }[event.key];
    if (!this.browse || step === undefined) return false;
    // Pas pendant la saisie du surnom (les flèches y déplacent le curseur).
    if ((event.target as Element | null)?.closest('input, textarea, select')) return false;
    event.preventDefault();
    if (step < 0) this.browse.previous(); else this.browse.next();
    return true;
  }

  setCollection(collection: OwnedMonsterView[]): void {
    this.collection = collection;
    if (this.isOpen) this.render();
  }

  private render(): void {
    const monster = this.collection.find((owned) => owned.id === this.monsterId);
    if (!monster) return this.close();
    const place = monster.slot === null ? withIcon('box', 'Dans la boîte') : monster.slot === 0 ? withIcon('star', 'En tête de l\'équipe') : [`Équipe · position ${monster.slot + 1}`];
    const caught = new Date(monster.caughtAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    const xpBar = el('div', { className: 'bar xp' }, el('div'));
    (xpBar.firstElementChild as HTMLElement).style.width = `${(100 * monster.xp) / Math.max(1, monster.xpNext)}%`;

    const rename = el('input', { value: monster.nickname ?? '', placeholder: monster.speciesName, maxLength: 20, className: 'sheet-rename' });
    rename.addEventListener('keydown', (event) => {
      event.stopPropagation(); // ne pas déplacer le joueur en tapant
      if (event.key === 'Enter') rename.blur();
    });
    rename.addEventListener('change', () => this.handlers.onRename(monster.id, rename.value));

    // En-tête toujours visible, puis un onglet à la fois : la fiche reste lisible sur un téléphone.
    const tabs = el('nav', { className: 'sheet-tabs' }, ...SHEET_TABS.map(({ id, label, icon: iconName }) => {
      const button = el('button', { className: id === this.tab ? 'active' : 'secondary' }, ...withIcon(iconName, label));
      button.addEventListener('click', () => { this.tab = id; this.render(); });
      return button;
    }));
    const content: Record<SheetTab, () => HTMLElement[]> = {
      summary: () => [
        el('label', { className: 'sheet-field' }, 'Surnom', rename),
        el('div', { className: 'sheet-bars' },
          el('small', { textContent: `Expérience ${monster.xp} / ${monster.xpNext} avant le niveau ${monster.level + 1}` }), xpBar),
        el('p', { className: 'muted sheet-info' }, ...place, ` · ${[`capturé le ${caught}`, monster.evolveLevel ? `évolue au niveau ${monster.evolveLevel}` : ''].filter(Boolean).join(' · ')}`),
        this.followButton(monster),
      ],
      moves: () => [this.movesSection(monster)],
      stats: () => [this.statsTable(monster)],
    };

    this.body.replaceChildren(
      el('div', { className: 'sheet-head' },
        el('img', { src: monster.image, alt: monster.name, className: `sheet-image ${monster.shiny ? 'shiny' : ''}` }),
        el('div', { className: 'sheet-id' },
          el('h2', {}, ...monsterName(monster)),
          el('p', { className: 'muted', textContent: `${monster.speciesName} · Niveau ${monster.level}` }),
          el('div', { className: 'badges' }, ...typeBadges(monster), rarityBadge(monster.rarity)),
          el('div', { className: 'sheet-bars' }, el('small', { textContent: `PV ${monster.hp} / ${monster.maxHp}` }), hpBar(monster.hp, monster.maxHp)))),
      ...(this.browse ? [this.browseBar(this.browse)] : []),
      tabs,
      el('div', { className: 'sheet-tab' }, ...content[this.tab]()),
      actionBar([this.giveAction(monster), ...this.actions], 'sheet-actions'));
  }

  private browseBar(browse: MonsterBrowse): HTMLElement {
    const arrow = (iconName: 'chevron-left' | 'chevron-right', label: string, go: () => void) => {
      const button = el('button', { className: 'secondary', title: `${label} (flèche ${iconName === 'chevron-left' ? 'gauche' : 'droite'})` }, icon(iconName, { label }));
      button.addEventListener('click', go);
      return button;
    };
    return el('nav', { className: 'sheet-browse', ariaLabel: creatureText('{Monstres} voisin{·e}s') },
      arrow('chevron-left', creatureText('{Monstre} précédent{·e}'), () => browse.previous()),
      el('span', { className: 'muted', textContent: browse.position }),
      arrow('chevron-right', creatureText('{Monstre} suivant{·e}'), () => browse.next()));
  }

  /** Attaques du monstre ; « Changer » ouvre la liste de toutes celles qu'il peut connaître à son niveau. */
  private movesSection(monster: OwnedMonsterView): HTMLElement {
    const editing = this.editingMoves;
    const card = (id: number, selected: boolean, onClick?: () => void) => {
      const move = this.catalog.get(id);
      return move ? moveCard(move, selected, onClick) : el('span');
    };
    const header = el('div', { className: 'sheet-moves-head' }, el('h3', {}, ...withIcon('moves', 'Attaques')));
    if (!editing) {
      const change = el('button', { className: 'secondary', textContent: 'Changer', disabled: monster.learnable.length === 0 });
      change.addEventListener('click', () => { this.editingMoves = [...monster.moves]; this.render(); });
      header.append(change);
      return el('div', { className: 'sheet-moves' }, header,
        el('div', { className: 'move-grid' }, ...(monster.moves.length ? monster.moves.map((id) => card(id, false))
          : [el('p', { className: 'hint', textContent: 'Aucune attaque : il se battra avec Lutte.' })])));
    }
    const toggle = (id: number) => {
      const list = this.editingMoves ?? [];
      this.editingMoves = list.includes(id) ? list.filter((other) => other !== id) : list.length < MAX_MOVES ? [...list, id] : list;
      this.render();
    };
    const save = el('button', { textContent: 'Enregistrer', disabled: editing.length === 0 });
    save.addEventListener('click', () => {
      this.handlers.onSetMoves(monster.id, editing);
      this.editingMoves = null;
    });
    const cancel = el('button', { className: 'secondary', textContent: 'Annuler' });
    cancel.addEventListener('click', () => { this.editingMoves = null; this.render(); });
    header.append(cancel, save);
    return el('div', { className: 'sheet-moves' }, header,
      el('p', { className: 'hint', textContent: `Choisissez jusqu'à ${MAX_MOVES} attaques (${editing.length}/${MAX_MOVES}) parmi celles que ${monster.name} peut connaître au niveau ${monster.level}.` }),
      el('div', { className: 'move-grid' }, ...monster.learnable.map((id) => card(id, editing.includes(id), () => toggle(id)))));
  }

  /** Seul un monstre de l'équipe peut suivre le joueur : dans la boîte, un rappel à la place du bouton. */
  private followButton(monster: OwnedMonsterView): HTMLElement {
    if (monster.slot === null) return el('p', { className: 'hint sheet-follow', textContent: creatureText('Mettez-{le} dans votre équipe pour qu\'{il} vous suive.') });
    const button = el('button', {
      className: monster.following ? 'secondary' : '',
      title: creatureText('{Le monstre} marche derrière vous ; les autres joueurs {le} voient aussi.'),
    });
    button.append(...withIcon('follow', monster.following ? 'Ne plus me suivre' : 'Me suivre'));
    button.addEventListener('click', () => this.handlers.onFollow(monster.following ? null : monster.id));
    return el('div', { className: 'sheet-follow' }, button);
  }

  private statsTable(monster: OwnedMonsterView): HTMLElement {
    const stars = Array.from({ length: 5 }, (_, i) => icon('star', { className: i < monster.stars ? 'star-on' : 'star-off' }));
    const rows = STAT_KEYS.map((key) => {
      const bonus = monster.bonus[key];
      const meter = el('span', { className: 'bonus-meter' }, el('i'));
      (meter.firstElementChild as HTMLElement).style.width = `${(100 * bonus) / BONUS_MAX}%`;
      return el('tr', {},
        el('th', { textContent: STAT_LABELS[key] }),
        el('td', { textContent: String(monster.base[key]) }),
        el('td', { className: 'bonus' }, el('div', {}, el('span', { textContent: `+${bonus}` }), meter)),
        el('td', { className: 'total', textContent: String(monster[STAT_OF[key]]) }));
    });
    return el('div', { className: 'sheet-stats' },
      el('div', { className: 'sheet-potential' }, el('strong', { textContent: 'Potentiel ' }),
        el('span', { className: `stars${isPerfectBonus(monster.bonus) ? ' perfect' : ''}`, title: isPerfectBonus(monster.bonus) ? 'Parfait : le maximum partout !' : `${monster.stars} sur 5, d'après ses bonus` }, ...stars)),
      el('table', {},
        el('thead', {}, el('tr', {}, el('th'), el('th', { textContent: 'Base' }), el('th', { textContent: `Bonus (0–${BONUS_MAX})` }), el('th', { textContent: `Au niv. ${monster.level}` }))),
        el('tbody', {}, ...rows)),
      el('p', { className: 'hint', textContent: creatureText('Les bonus sont propres à chaque {monstre}, tirés à sa rencontre : deux {monstres} de la même espèce n\'ont pas les mêmes statistiques.') }));
  }

  /** Ouvre le sac pour donner un objet (potions…) au monstre. */
  private giveAction(monster: OwnedMonsterView): CardAction {
    return { label: 'Donner un objet', icon: 'bag', title: 'Choisir dans le sac un objet à lui donner', onClick: () => this.handlers.onGiveItem(monster), variant: 'secondary' };
  }
}
